<template>
  <div
    v-if="itemKeys.length"
    :class="{
      'idf-carousel--background': !!background,
      'idf-carousel--allItemsVisible': isAllVisible,
      'idf-carousel--mustScroll': mustScrollVisible,
    }"
    :style="`background-color: ${(background && background.color) || 'inherit'};`"
    class="idf-carousel"
    data-test-carousel
  >
    <adn-image
      v-if="background && background.url"
      :max-width-to-autosize="1100"
      :source="background.url"
      class="idf-carousel__background-image"
    ></adn-image>

    <div class="idf-carousel__content">
      <header v-if="$slots.header || $slots.title || value || (mustScrollVisible && type === 'TopButton')">
        <div v-if="$slots.header">
          <slot name="header"></slot>
        </div>
        <div v-if="$slots.title || value || (mustScrollVisible && type === 'TopButton')">
          <div class="slot-title">
            <slot name="title">
              <div v-if="value">
                <h2 v-if="value.title">{{ value.title }}</h2>
                <p v-if="value.description">{{ value.description }}</p>
              </div>
            </slot>
          </div>
          <div v-if="mustScrollVisible && type === 'TopButton'" class="scroll-manager scroll-manager--top">
            <button
              class="scroll-button"
              :aria-disabled="items[0].visibility"
              :class="{ 'btn--disabled': items[0].visibility }"
              type="button"
              @click="previousElement"
            >
              <span aria-label="Précédent" class="adn-icon adn-icon--navigation-arrow-back" />
            </button>
            <button
              class="scroll-button"
              :aria-disabled="items[items.length - 1].visibility"
              :class="{ 'btn--disabled': items[items.length - 1].visibility }"
              type="button"
              @click="nextElement"
            >
              <span aria-label="Suivant" class="adn-icon adn-icon--navigation-arrow-right" />
            </button>
          </div>
        </div>
      </header>

      <div class="content">
        <div v-if="mustScrollVisible && type === 'InlineButton'" class="scroll-manager">
          <button
            class="scroll-button"
            :aria-disabled="items[0].visibility"
            :class="{ 'btn--disabled': items[0].visibility }"
            type="button"
            @click="previousElement"
          >
            <span aria-label="Précédent" class="adn-icon adn-icon--navigation-arrow-back" />
          </button>
        </div>

        <ul ref="itemsContainer" data-test-itemsContainer>
          <div v-for="itemKey of itemKeys" :key="itemKey" :data-index="itemKey">
            <slot v-if="canRenderTheSlot(itemKey)" :name="itemKey" />
          </div>
        </ul>

        <div v-if="mustScrollVisible && type === 'InlineButton'" class="scroll-manager">
          <button
            class="scroll-button"
            :aria-disabled="items[items.length - 1].visibility"
            :class="{ 'btn--disabled': items[items.length - 1].visibility }"
            type="button"
            @click="nextElement"
          >
            <span aria-label="Suivant" class="adn-icon adn-icon--navigation-arrow-right" />
          </button>
        </div>
      </div>

      <footer v-if="$slots.footer">
        <slot name="footer"></slot>
      </footer>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, type PropType, ref, type Ref, useSlots } from 'vue'
import AdnImage from '../AdnImage/AdnImage.vue'

const props = defineProps({
  background: {
    type: Object as PropType<{ color?: string; url?: string }>,
    required: false,
  },
  value: {
    type: Object as PropType<{ title?: string; description?: string }>,
    required: false,
  },
  type: {
    type: String as PropType<'InlineButton' | 'TopButton'>,
    default: 'TopButton',
  },
  itemListLength: {
    type: Number,
  },
})

const emit = defineEmits(['itemsVisibilityChange'])
const slots = useSlots()
const itemsContainer = ref<HTMLElement | null>(null)
const items: Ref<{ htmlElt: HTMLElement; visibility: boolean }[]> = ref([])
const isAllVisible: Ref<boolean> = ref(false)
const mustScrollVisible: Ref<boolean> = ref(false)
const visibleItemsIdx: Ref<Array<number>> = ref([])

const renderAllElement = ref(false)
defineExpose({
  visibleItemsIdx,
})

const itemKeys = computed(() => {
  if (props.itemListLength) return Array.from({ length: props.itemListLength }, (_, i) => i)
  return Object.keys(slots).filter((key) => !['title', 'header', 'footer'].includes(key))
})

let observer
let widthToSlide = 0
let gap = 0
let eventSending

function scrollListener() {
  renderAllElement.value = true
  itemsContainer.value?.removeEventListener('scroll', scrollListener)
}

function debouncedVisibilityEvent() {
  if (eventSending) clearTimeout(eventSending)
  eventSending = setTimeout(() => {
    emit('itemsVisibilityChange', visibleItemsIdx.value)
  }, 200)
}

function onVisibilityChange(entries) {
  const tmpItems = [...items.value]
  entries.forEach((entry) => {
    tmpItems[entry.target.dataset.index].visibility = entry.isIntersecting
  })
  items.value = tmpItems

  const visibleItems = tmpItems.filter((elt) => elt.visibility)
  const allVisibleItemsWidth = visibleItems
    .map((item) => item.htmlElt.offsetWidth)
    .reduce((acc, current) => acc + current, 0)

  visibleItemsIdx.value = visibleItems.map((elt) => tmpItems.indexOf(elt))
  isAllVisible.value = visibleItems.length === tmpItems.length
  mustScrollVisible.value = visibleItems.length < tmpItems.length

  widthToSlide = allVisibleItemsWidth + gap * visibleItems.length
  if (mustScrollVisible.value && widthToSlide === 0) {
    widthToSlide = tmpItems[0].htmlElt.offsetWidth
  }
  debouncedVisibilityEvent()
}

onMounted(() => {
  observer = new IntersectionObserver(onVisibilityChange, {
    root: itemsContainer.value as Element,
    rootMargin: '0px',
    threshold: 1,
  })

  let firstElt
  let secondElt

  items.value = Array.from((itemsContainer.value && itemsContainer.value.children) || []).map((child, index) => {
    if (index === 0) {
      firstElt = child as HTMLElement
    }
    if (index === 1) {
      secondElt = child as HTMLElement
    }
    observer.observe(child)
    return { htmlElt: child as HTMLElement, visibility: false }
  })

  if (firstElt && secondElt) {
    gap = secondElt.offsetLeft - (firstElt.offsetLeft + firstElt.offsetWidth)
  }

  itemsContainer.value?.addEventListener('scroll', scrollListener)
})

onBeforeUnmount(() => {
  itemsContainer.value?.removeEventListener('scroll', scrollListener)
  observer?.disconnect()
})

const previousElement = () => {
  if (itemsContainer.value) {
    itemsContainer.value.scrollBy({ left: -widthToSlide, behavior: 'smooth' })
  }
}

const nextElement = () => {
  if (itemsContainer.value) {
    renderAllElement.value = true
    setTimeout(
      () => {
        itemsContainer.value?.scrollBy({ left: widthToSlide, behavior: 'smooth' })
      },
      renderAllElement.value ? 0 : 100,
    )
  }
}

const canRenderTheSlot = computed(() => {
  return (itemKey: number | string) =>
    typeof window === 'undefined' ||
    !props.itemListLength ||
    renderAllElement.value ||
    items.value[itemKey]?.visibility ||
    itemKey === visibleItemsIdx.value.length
})
</script>

<style lang="scss">
.idf-carousel {
  border: none;
  border-radius: var(--border-radius-l);
  position: relative;
  overflow: hidden;
  width: 100%;

  &__background-image {
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    img {
      object-fit: cover;
      object-position: center;
    }
  }

  &__content {
    padding: var(--spacing-3);
    position: relative;
    z-index: 2;

    header {
      margin-bottom: var(--spacing-3);

      > :last-child {
        align-items: var(--idf-carousel-title-align-items, flex-start);
        display: flex;
        gap: var(--spacing-4);

        .slot-title {
          flex: 1 1 0;
        }

        .scroll-manager--top {
          align-items: center;
          display: flex;
          flex: 0 0 auto;
          flex-wrap: nowrap;
          gap: var(--spacing-3);
        }
      }

      h2 {
        margin-bottom: 0;
      }
    }

    .content {
      align-items: center;
      display: flex;
      gap: var(--spacing-3);

      ul {
        display: flex;
        flex-wrap: nowrap;
        gap: var(--idf-carousel-items-gap, var(--spacing-3));
        justify-content: var(--idf-carousel-items-justify-content, flex-start);
        overflow: hidden;
        width: calc(100% + 0.8rem);

        li {
          flex: 0 0 auto;

          > * {
            min-height: 100% !important; // pour garantir que tous les items sont de la même hauteur
          }
        }
      }
    }

    footer {
      margin-top: var(--spacing-4);
    }

    .scroll-button {
      background-color: var(--color-background-light);
      border: none;
      border-radius: var(--border-radius-m);
      color: var(--color-main-dark);
      cursor: pointer;
      height: 40px;
      outline: none;
      padding: 0;
      transition: transform 0.02s;
      width: 40px;

      &:hover {
        transform: scale(1.1);
      }

      &.btn--disabled {
        color: var(--color-inactive-light);
        cursor: default;

        &:hover {
          transform: none;
        }
      }
    }
  }

  &.idf-carousel--background {
    .idf-carousel__content {
      padding: var(--spacing-6);

      header .slot-title {
        color: var(--color-white);
      }
    }
  }

  &.idf-carousel--mustScroll .content ul {
    justify-content: flex-start;
  }
}

@media screen and (width <= calc($breakpoint-md - 1px)) {
  .idf-carousel {
    &__content {
      .scroll-manager,
      header > :last-child .scroll-manager {
        display: none;
      }

      header {
        :empty(&) {
          margin-bottom: 0;
        }

        > :last-child {
          flex-direction: column;
          gap: 0;
        }
      }

      .content ul {
        justify-content: flex-start;
        overflow-x: auto;
        padding-bottom: var(--spacing-3);
        scroll-snap-type: x mandatory;

        li {
          scroll-snap-align: start;
        }
      }
    }

    &.idf-carousel--background .idf-carousel__content {
      padding: var(--spacing-3);
    }
  }
}
</style>
